import { ChangeInputEvent } from '@helpers/types.ts';
import { generateZPL, generateZPLContainer } from '@helpers/zpl.ts';
import useContainers from '@hooks/use-containers.ts';
import { useLogin } from '@hooks/use-login.ts';
import { usePrinter } from '@hooks/use-printer.ts';
import { format } from 'date-fns';
import { chain, find } from 'lodash';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useSound from 'use-sound';
import siren from '@assets/sounds/siren_alert.mp3';
import {
  IStationSwitcherContext,
  StationSwitcherContext,
} from '@components/providers/station/context.tsx';
import {
  InductionRoute,
  RouteOrder,
} from '@helpers/types';
import { latLngToCell } from 'h3-js';
import { useHttp } from '@hooks/use-http';

interface RouteOrderWithContainer extends RouteOrder {
  disabled: boolean;
  plan_id: string;
  route_id: string;
  route_name: string;
  container_id: string;
  container_name: string;
}

export const useInductionArea = (
  routes: Array<InductionRoute>,
  _onClose: () => void,
) => {
  const http = useHttp();
  const { station: currStation } =
    useContext<IStationSwitcherContext>(StationSwitcherContext);
  // @ts-ignore
  const [playAlert] = useSound(siren);
  const { email } = useLogin();
  const {
    onAddToContainer,
    onToggleContainer,
    containers: containers_,
  } = useContainers(currStation?.country ?? '', currStation?.station ?? '');
  const [progress, setProgress] = useState<any>({});
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [toInduct, setToInduct] = useState<string>('');
  const [loading, _setLoading] = useState<boolean>(false);
  const [inductedOnly, setInducted] = useState<boolean>(false);
  const [success_induct, setSuccessInduct] =
    useState<RouteOrderWithContainer | null>(null);
  const { printer, printZpl, connectDevice } = usePrinter();
  // ----------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------
  const orders: Array<RouteOrderWithContainer> = useMemo(() => {
    return routes
      .map((route) =>
        route.orders.map((order) => ({
          ...order,
          disabled: route.disabled ?? false,
          route_name: route.name,
          route_id: route.id,
          plan_id: route.plan_id,
          container_id: route.container,
          container_name: route.container_name,
        })),
      )
      .flat();
  }, [routes]);

  const plan_ids = useMemo(() => {
    return chain(routes)
      .map((r) => r.plan_id)
      .uniq()
      .value();
  }, [routes]);
  const route_names = useMemo(() => {
    return chain(routes)
      .map((r) => r.name)
      .uniq()
      .value();
  }, [routes]);

  const clearInput = useCallback(() => {
    setToInduct('');
    inputRef.current!.focus();
  }, []);

  // ----------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------
  const prepareInduction = useCallback(async () => {
    setError('');
    setInducted(false);
    setSuccessInduct(null);
    const cleanInduct = toInduct.trim();
    const order = find(
      orders,
      (o) =>
        o.tracking_id.toString() === cleanInduct ||
        o.internal_key == cleanInduct,
    );

    if (!order || (!!order && order.disabled)) {
      await http.induction.induct({
        user: email,
        station: currStation?.station ?? '',
        country: currStation?.country ?? '',
        tracking_id: cleanInduct,
      });
      setInducted(true);
      // @ts-ignore
      setSuccessInduct({
        tracking_id: parseInt(cleanInduct),
      });
      clearInput();
      return true;
    } else {
      const now = format(new Date(), 'dd/MM/y');
      const source = `LastMile | ${email} | Planificador`;

      try {
        await onToggleContainer(order!.container_id, true);
        const response = await onAddToContainer(
          order.tracking_id.toString(),
          order!.container_id,
          source,
          order.route_name,
          order.delivery_order,
          0,
          latLngToCell(
            order.geometry.coordinates[1] as number,
            order.geometry.coordinates[0] as number,
            13,
          ),
          false,
        );

        if (
          response.status != 'FOUND' ||
          response.containerid == order?.container_id
        ) {
          setSuccessInduct(order);
          const ticket = generateZPL(
            order.container_name,
            order.route_name,
            cleanInduct,
            order.delivery_order.toString(),
            '0',
            now,
            'N/A',
          );
          console.log(ticket);
          http.lastmile.setInduct(order.plan_id, order.route_name, order.tracking_id).then(
            () => {
              http.lastmile.getInductProgress(plan_ids, route_names).then((progress) => {
                setProgress(progress);
              });
            },
          );
          if (!order.disabled) {
            await printZpl(ticket);
          }
          setToInduct('');
          inputRef.current!.focus();
          return true;
        } else {
          clearInput();
          setError('El paquete ya esta en el contenedor');
          return false;
        }
      } catch (e: any) {
        clearInput();
        setError(e.response?.data.error || e.message);
        return false;
      }
    }
  }, [
    toInduct,
    orders,
    email,
    currStation?.station,
    currStation?.country,
    clearInput,
    http,
    onToggleContainer,
    onAddToContainer,
    printZpl,
    plan_ids,
    route_names,
  ]);

  const onChangeTracking = useCallback(
    ({ target: { value } }: ChangeInputEvent) => setToInduct(value),
    [],
  );

  const onKeyEnter = useCallback(
    (e: any) => {
      if (e.key === 'Enter' || e.keyCode == 13) {
        prepareInduction().then((w) => {
          if (!w) {
            playAlert();
          }
        });
      }
    },
    [prepareInduction, playAlert],
  );

  const onPrintContainer = useCallback(
    async (container: string, container_name: string, route: string) => {
      const ticket = generateZPLContainer(container, container_name, route);
      console.log(ticket);
      await printZpl(ticket);
    },
    [printZpl],
  );

  useEffect(() => {
    if (plan_ids.length > 0 && route_names.length > 0)
      http.lastmile.getInductProgress(plan_ids, route_names).then((progress) => {
        setProgress(progress);
      });
  }, [plan_ids, route_names, http]);

  return {
    error,
    loading,
    inputRef,
    toInduct,
    currStation,
    progress,
    onKeyEnter,
    inductedOnly,
    connectDevice,
    success_induct,
    onChangeTracking,
    onPrintContainer,
    connectedPrinter: !!printer?.current,
    inducted_containers: containers_,
  };
};
